import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '../lib/supabaseClient';
import type { Session, User as SupabaseUser } from '@supabase/supabase-js';

interface User {
  id: string;
  name: string;
  email: string;
  role: 'admin' | 'team_member' | 'viewer';
  isDemo: boolean;
  companyId?: string;
  companyName?: string;
  phone?: string;
}

interface SignupOptions {
  name: string;
  phone?: string;
  company?: string;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string, options: SignupOptions) => Promise<{ requiresEmailConfirmation: boolean }>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  resendConfirmationEmail: (email: string) => Promise<void>;
  isAuthenticated: boolean;
  isDemo: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

const DEMO_EMAIL = 'demo@mtaplannerpro.com';
const DEMO_PASSWORD = 'demo123';

const buildUserFromProfile = (
  supabaseUser: SupabaseUser,
  profile: { full_name?: string; role?: string; company_id?: string; email?: string } | null,
  companyName?: string
): User => ({
  id: supabaseUser.id,
  name: profile?.full_name || supabaseUser.email?.split('@')[0] || 'User',
  email: supabaseUser.email || '',
  role: (profile?.role as User['role']) || 'admin',
  isDemo: supabaseUser.email === DEMO_EMAIL,
  companyId: profile?.company_id || undefined,
  companyName: companyName || undefined,
});

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  const loadProfile = async (supabaseUser: SupabaseUser) => {
    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('full_name, role, company_id, email')
        .eq('id', supabaseUser.id)
        .maybeSingle();

      let companyName: string | undefined;
      if (profile?.company_id) {
        const { data: company } = await supabase
          .from('companies')
          .select('name')
          .eq('id', profile.company_id)
          .maybeSingle();
        companyName = company?.name;
      }

      setUser(buildUserFromProfile(supabaseUser, profile, companyName));
    } catch (err) {
      console.error('Failed to load profile:', err);
      setUser(buildUserFromProfile(supabaseUser, null));
    }
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s);
      if (s?.user) {
        loadProfile(s.user).finally(() => setIsInitialized(true));
      } else {
        setIsInitialized(true);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
      if (s?.user) {
        loadProfile(s.user);
      } else {
        setUser(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const login = async (email: string, password: string) => {
    // Demo shortcut — use real Supabase auth if demo account exists, else mock
    if (
      email.toLowerCase() === DEMO_EMAIL.toLowerCase() &&
      password === DEMO_PASSWORD
    ) {
      // Try real login first; if it fails, fall back to a local demo session
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) {
        // Local demo fallback
        const demoUser: User = {
          id: 'demo-user-001',
          name: 'Demo User',
          email: DEMO_EMAIL,
          role: 'admin',
          isDemo: true,
          companyName: 'Demo Construction Co.',
        };
        setUser(demoUser);
        return;
      }
      return;
    }

    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw new Error(error.message);
  };

  const signup = async (email: string, password: string, options: SignupOptions): Promise<{ requiresEmailConfirmation: boolean }> => {
    // 1. Create the Supabase auth user
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: options.name,
          phone: options.phone || '',
        },
      },
    });

    if (authError) throw new Error(authError.message);
    if (!authData.user) throw new Error('Signup failed — no user returned.');

    // If session is null, Supabase requires email confirmation before the user is active
    const requiresEmailConfirmation = !authData.session;

    const userId = authData.user.id;

    // 2. Create a company row (if company name provided)
    let companyId: string | null = null;
    const companyName = options.company?.trim();

    if (companyName) {
      const { data: companyData, error: companyError } = await supabase
        .from('companies')
        .insert({ name: companyName })
        .select('id')
        .single();

      if (companyError) {
        console.error('Company insert error:', companyError);
      } else {
        companyId = companyData.id;
      }
    }

    // 3. Create the profile row
    const { error: profileError } = await supabase
      .from('profiles')
      .insert({
        id: userId,
        full_name: options.name,
        email,
        role: 'admin',
        company_id: companyId,
      });

    if (profileError) {
      console.error('Profile insert error:', profileError);
    }

    // 4. Only set local user state if session is active (no email confirmation needed)
    if (!requiresEmailConfirmation) {
      setUser({
        id: userId,
        name: options.name,
        email,
        role: 'admin',
        isDemo: false,
        companyId: companyId || undefined,
        companyName: companyName || undefined,
      });
    }

    return { requiresEmailConfirmation };
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/forgot-password`,
    });
    if (error) throw new Error(error.message);
  };

  const resendConfirmationEmail = async (email: string) => {
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email,
    });
    if (error) throw new Error(error.message);
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
  };

  if (!isInitialized) return null;

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        login,
        signup,
        logout,
        resetPassword,
        resendConfirmationEmail,
        isAuthenticated: !!user,
        isDemo: user?.isDemo || false,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
